import { useState, useEffect, useRef, useCallback } from 'react';
import { AlertCircle, Check, RefreshCw } from 'lucide-react';
import { type MetricFormat, type MetricDefinition } from '@monitoring-panel/types';
import { Modal } from './Modal';
import { api } from '@/services/api';
import { cn, formatNumber } from '@/lib/utils';

interface CustomMetricBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  availableColumns: string[];
  sampleData: Record<string, number>;
  editingMetric?: MetricDefinition | null;
  onCreate: (metric: { name: string; formula: string; format: MetricFormat; decimals: number }) => void;
  onUpdate: (metricId: string, metric: { name: string; formula: string; format: MetricFormat; decimals: number }) => void;
}

const OPERATORS = ['+', '-', '*', '/', '(', ')'] as const;
const FORMATS: { value: MetricFormat; label: string }[] = [
  { value: 'percentage', label: 'Percentage (%)' },
  { value: 'euro', label: 'Euro (€)' },
  { value: 'dollar', label: 'Dollar ($)' },
  { value: 'number', label: 'Number (#)' },
  { value: 'decimal', label: 'Decimal (0.00)' },
];

interface ValidationResult {
  valid: boolean;
  dependencies: string[];
  missingColumns?: string[];
  error?: string;
  preview?: number;
}

export function CustomMetricBuilder({
  isOpen,
  onClose,
  availableColumns,
  sampleData,
  editingMetric,
  onCreate,
  onUpdate,
}: CustomMetricBuilderProps) {
  const isEditMode = !!editingMetric;
  const [name, setName] = useState('');
  const [formula, setFormula] = useState('');
  const [format, setFormat] = useState<MetricFormat>('percentage');
  const [decimals, setDecimals] = useState(2);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const formulaInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Filter to only show numeric columns (those present in sampleData with numeric values)
  const numericColumns = availableColumns.filter(
    (col) => col in sampleData && typeof sampleData[col] === 'number'
  );

  // Validation function (defined before useEffects that use it)
  const validateFormula = useCallback(
    async (formulaToValidate: string) => {
      if (!formulaToValidate.trim()) {
        setValidation(null);
        return;
      }

      setIsValidating(true);
      try {
        const result = await api.config.validateFormula({
          formula: formulaToValidate,
          availableColumns,
          sampleData,
        });
        setValidation(result);
      } catch {
        setValidation({
          valid: false,
          dependencies: [],
          error: 'Failed to validate formula',
        });
      } finally {
        setIsValidating(false);
      }
    },
    [availableColumns, sampleData]
  );

  // Reset or populate state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (editingMetric) {
        setName(editingMetric.name);
        setFormula(editingMetric.formula);
        setFormat(editingMetric.format);
        setDecimals(editingMetric.decimals ?? 2);
        // Validate the existing formula immediately when editing
        setValidation(null);
        validateFormula(editingMetric.formula);
      } else {
        setName('');
        setFormula('');
        setFormat('percentage');
        setDecimals(2);
        setValidation(null);
      }
    }
  }, [isOpen, editingMetric, validateFormula]);

  // Trigger validation on formula change (debounced)
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      validateFormula(formula);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [formula, validateFormula]);

  const insertAtCursor = (text: string) => {
    const input = formulaInputRef.current;
    if (!input) {
      setFormula((prev) => prev + text);
      return;
    }

    const start = input.selectionStart ?? formula.length;
    const end = input.selectionEnd ?? formula.length;
    const newFormula = formula.substring(0, start) + text + formula.substring(end);
    setFormula(newFormula);

    // Set cursor position after inserted text
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(start + text.length, start + text.length);
    }, 0);
  };

  const handleSave = () => {
    if (!name.trim() || !formula.trim() || !validation?.valid) return;
    const metricData = { name: name.trim(), formula: formula.trim(), format, decimals };

    if (isEditMode && editingMetric) {
      onUpdate(editingMetric.id, metricData);
    } else {
      onCreate(metricData);
    }
    onClose();
  };

  const isValid = validation?.valid && !validation.missingColumns?.length;
  const canSave = name.trim() && formula.trim() && isValid;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditMode ? 'Edit Custom Metric' : 'Create Custom Metric'} maxWidth="lg">
      <div className="space-y-4">
        {/* Name Input */}
        <div>
          <label className="adv-field-label">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Cost per Visit"
            className="adv-input w-full"
          />
        </div>

        {/* Formula Input */}
        <div>
          <label className="adv-field-label">Formula</label>
          <input
            ref={formulaInputRef}
            type="text"
            value={formula}
            onChange={(e) => setFormula(e.target.value)}
            placeholder="e.g., campaignCost / campaignVisits"
            className="adv-input w-full adv-num"
          />
        </div>

        {/* Available Columns and Operators */}
        <div className="grid grid-cols-2 gap-4">
          {/* Columns */}
          <div>
            <label className="adv-field-label">
              Available Columns
            </label>
            <div className="h-40 overflow-y-auto rounded-adv-lg p-2 bg-adv-sunken">
              <div className="flex flex-wrap gap-1">
                {numericColumns.map((col) => (
                  <button
                    key={col}
                    onClick={() => insertAtCursor(col)}
                    className="px-2 py-1 text-adv-code-xs font-adv-mono text-adv-primary bg-adv-card border border-adv-border-default rounded-adv-sm hover:border-adv-gold hover:bg-adv-gold-veil transition-colors focus-visible:outline-none focus-visible:shadow-adv-focus"
                  >
                    {col}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Operators and Format */}
          <div className="space-y-3">
            <div>
              <label className="adv-field-label">Operators</label>
              <div className="flex gap-1">
                {OPERATORS.map((op) => (
                  <button
                    key={op}
                    onClick={() => insertAtCursor(` ${op} `)}
                    className="w-10 h-10 text-adv-body-l font-adv-mono text-adv-primary bg-adv-sunken border border-adv-border-default rounded-adv-md hover:border-adv-gold hover:bg-adv-gold-veil transition-colors focus-visible:outline-none focus-visible:shadow-adv-focus"
                  >
                    {op}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label className="adv-field-label">Format</label>
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value as MetricFormat)}
                  className="adv-input w-full"
                >
                  {FORMATS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-20">
                <label className="adv-field-label">Decimals</label>
                <input
                  type="number"
                  min={0}
                  max={10}
                  value={decimals}
                  onChange={(e) => setDecimals(Math.max(0, Math.min(10, parseInt(e.target.value) || 0)))}
                  className="adv-input w-full adv-num"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Validation Status */}
        <div
          className={cn(
            'p-3 rounded-adv-md text-adv-body-m',
            !formula.trim() && 'bg-adv-sunken',
            formula.trim() && isValidating && 'bg-adv-info-bg',
            formula.trim() && !isValidating && isValid && 'bg-adv-success-bg',
            formula.trim() && !isValidating && !isValid && 'bg-adv-warning-bg'
          )}
        >
          {!formula.trim() ? (
            <p className="text-adv-secondary">Enter a formula to validate</p>
          ) : isValidating ? (
            <div className="flex items-center gap-2 text-adv-info">
              <RefreshCw size={14} className="animate-spin" />
              <span>Validating...</span>
            </div>
          ) : validation?.valid && !validation.missingColumns?.length ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-adv-success">
                <Check size={14} />
                <span className="font-bold">Valid formula</span>
              </div>
              {validation.dependencies.length > 0 && (
                <p className="text-adv-body-s text-adv-secondary">
                  Dependencies: <span className="font-adv-mono">{validation.dependencies.join(', ')}</span>
                </p>
              )}
              {validation.preview !== undefined && (
                <p className="font-bold text-adv-primary">
                  Preview: <span className="adv-num">{formatNumber(validation.preview, format, decimals)}</span>
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-adv-warning-text">
                <AlertCircle size={14} />
                <span className="font-bold">
                  {validation?.error || 'Invalid formula'}
                </span>
              </div>
              {validation?.missingColumns && validation.missingColumns.length > 0 && (
                <p className="text-adv-body-s text-adv-warning-text">
                  Missing columns: <span className="font-adv-mono">{validation.missingColumns.join(', ')}</span>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="adv-btn adv-btn--ghost"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="adv-btn adv-btn--primary"
          >
            {isEditMode ? 'Update Metric' : 'Create Metric'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
